import { useEffect, useMemo, useState } from "react";
import { makeStyles } from "tss-react/mui";
import {
  MiButton,
  MiCheckbox,
  MiIcon,
  MiIconButton,
  MiInputTextbox,
  MiModalConfirm,
} from "@miplatform/components";
import {
  BUTTON_COLORS,
  BUTTON_VARIANTS,
  DATE_TIME_FORMATS,
  ICONS_WEB,
} from "@miplatform/constants";
import { InvoiceItemVarianceType, InvoiceStatus } from "@miplatform/types";
import { bugsnag, createUUID, formatDate } from "@miplatform/utils";
import * as mdi from "@mdi/js";
import { Box, Typography } from "@mui/material";
import {
  INVOICE_ITEM_TYPE,
  INVOICING_ROUTES,
  RECONCILIATION_STEPS,
} from "../../accountingConstants";
import {
  INVOICE_ITEM_DIFF_CONSTANTS,
  getInvoiceItemDiff,
} from "../../utils/utils";
import { useReconcileContext } from "../ReconcileContext";
import { formatOpenBalanceDisplay } from "../reconcileConstants";
import { AccountingInvoiceItem } from "./AccountingInvoiceItem";

const INITIAL_INVOICE_ITEM = {
  name: "",
  description: "",
};

const listRelatedInvoices = (invoices) => {
  return invoices
    .map((inv, index) => {
      const isLast = index === invoices.length - 1;
      const isSecondLast = index === invoices.length - 2;

      let separator = ", ";
      if (isLast) separator = "";
      else if (isSecondLast) separator = " and ";

      return `${inv.name} | ${inv.number}${separator}`;
    })
    .join("");
};

export const AccountingInvoice = ({
  invoice,
  invoicesCount,
  invoiceHeaderNumber,
}) => {
  const {
    order,
    invoicesModule,
    activeStep,
    fetchAndEnsureDraftInvoice,
    getPurchaseOrders,
    isInvoiceMode,
    verificationRef,
    verificationState,
    getVarianceColor,
    updateInvoiceFormState,
    viewMode,
    setInvoices,
    clearItemVerification,
    setItemVerificationStatus,
  } = useReconcileContext();
  const { classes, theme } = useStyles({ activeStep });
  const [tempInvoiceItems, setTempInvoiceItems] = useState([]);
  const [paymentItems, setPaymentItems] = useState([]);
  const [tempPaymentItems, setTempPaymentItems] = useState([]);
  const [localInvoiceItems, setLocalInvoiceItems] = useState([]);
  const [localInvoiceVarianceItems, setLocalInvoiceVarianceItems] = useState(
    []
  );
  const [newInvoiceName, setNewInvoiceName] = useState("");
  const [invoiceNameError, setInvoiceNameError] = useState(null);
  const [confirmModalConfig, setConfirmModalConfig] = useState({
    open: false,
    title: "",
    description: "",
    textPositive: "Confirm",
    textNegative: "Cancel",
    handlePositive: () => {},
    handleNegative: () =>
      setConfirmModalConfig((prev) => ({ ...prev, open: false })),
  });

  const { name: invoiceName = "", invoiceNumber = "" } = invoice || {};

  useEffect(() => {
    if (!verificationRef.current) {
      verificationRef.current = new Map();
    }

    const updatedItems = (invoice?.invoiceItems || [])
      .filter(
        (item) =>
          item.invoiceItemVarianceType !== InvoiceItemVarianceType.Variance
      )
      .map((item) => {
        if (invoice.status !== InvoiceStatus.Draft)
          // only verify items belong the draft invoices
          return { ...item, isVerified: true };
        const isVerified = verificationRef.current.get(item.id);
        if (isVerified === undefined) {
          setItemVerificationStatus(item.id, false);
        }
        return isVerified !== undefined ? { ...item, isVerified } : item;
      });
    setLocalInvoiceVarianceItems(
      invoice?.invoiceItems.filter(
        (item) =>
          item.invoiceItemVarianceType === InvoiceItemVarianceType.Variance
      )
    );

    setLocalInvoiceItems(updatedItems);
  }, [invoice?.invoiceItems, verificationRef, setItemVerificationStatus]);

  useEffect(() => {
    if (!verificationRef.current) {
      verificationRef.current = new Map();
    }
    const updatedItems = (invoice?.invoiceBillingCommitments || []).map(
      (item) => {
        if (invoice.status !== InvoiceStatus.Draft)
          return { ...item, isVerified: true };
        const verificationKey = item.billingCommitmentId + invoice.id;
        const isVerified = verificationRef.current.get(verificationKey);
        if (isVerified === undefined) {
          setItemVerificationStatus(verificationKey, false);
        }
        return isVerified !== undefined ? { ...item, isVerified } : item;
      }
    );

    setPaymentItems(updatedItems);
  }, [
    invoice?.invoiceBillingCommitments,
    verificationRef,
    setItemVerificationStatus,
    invoice.id,
  ]);

  const combinedInvoiceItems = useMemo(() => {
    return [...localInvoiceItems, ...tempInvoiceItems];
  }, [localInvoiceItems, tempInvoiceItems]);

  const combinedInvoicePayments = useMemo(() => {
    return [
      ...paymentItems.map((item) => ({
        ...item,
        id: item.billingCommitmentId + invoice.id,
      })),
      ...tempPaymentItems,
    ];
  }, [paymentItems, tempPaymentItems]);

  const filteredInvoiceItems = useMemo(() => {
    if (activeStep === RECONCILIATION_STEPS.REVIEW_SUBMIT) {
      return combinedInvoiceItems.filter((item) => !item.isNew);
    }
    if (isInvoiceMode) {
      if (
        combinedInvoiceItems.length === 0 &&
        invoice?.invoiceItems?.length > 0
      ) {
        return invoice.invoiceItems.filter(
          (item) =>
            item.invoiceItemVarianceType !== InvoiceItemVarianceType.Variance
        );
      }
      return getInvoiceItemDiff(
        combinedInvoiceItems,
        (invoice?.reconciliationSnapshotInvoiceItems || []).filter(
          (item) =>
            item.invoiceItemVarianceType !== InvoiceItemVarianceType.Variance
        ) || [],
        true
      );
    }
    return combinedInvoiceItems;
  }, [combinedInvoiceItems, activeStep]);

  const totalDraw = useMemo(() => {
    return filteredInvoiceItems
      .filter((item) => item.status !== INVOICE_ITEM_DIFF_CONSTANTS.DELETED)
      .reduce((sum, item) => {
        const unitPrice = parseFloat(item.unitPrice) || 0;
        const quantity = parseFloat(item.quantity) || 1;
        return sum + unitPrice * quantity;
      }, 0);
  }, [filteredInvoiceItems]);

  const { totalPaidOnPO, totalVariance } = useMemo(() => {
    const totalPaidOnPO = combinedInvoicePayments.reduce((sum, item) => {
      return sum + (parseFloat(item.paidOnBillingCommitment) || 0);
    }, 0);

    return {
      totalPaidOnPO,
      totalVariance: invoice.variance,
    };
  }, [combinedInvoicePayments, totalDraw]);
  const handleAddInvoiceItem = () => {
    if (invoice?.isNew && !newInvoiceName?.trim()) {
      setInvoiceNameError({
        message: "Please enter an invoice name before adding items",
      });
      return;
    }

    const tempId = createUUID();
    const newItem = {
      ...INITIAL_INVOICE_ITEM,
      id: tempId,
      section: "Accounting Invoice",
      tempId,
      isNew: true,
      type: INVOICE_ITEM_TYPE.OTHER,
    };
    setTempInvoiceItems((prev) => [...prev, newItem]);
  };

  const handleAddPaymentInformation = () => {
    if (invoice?.isNew && !newInvoiceName?.trim()) {
      setInvoiceNameError({
        message: "Please enter an invoice name before adding items",
      });
      return;
    }
    const tempId = createUUID();
    const newItem = {
      id: tempId,
      tempId,
      isNew: true,
    };
    setTempPaymentItems((prev) => [...prev, newItem]);
  };

  const handleDeleteInvoiceItem = async (invoiceItem) => {
    const isTempItem = tempInvoiceItems.some(
      (item) => item.id === invoiceItem.id
    );
    if (isTempItem) {
      setTempInvoiceItems((prev) =>
        prev.filter((item) => item.id !== invoiceItem.id)
      );
      clearItemVerification(invoiceItem.id);
      updateInvoiceFormState(invoiceItem.id, null);
      return;
    }
    const executeDelete = async () => {
      setLocalInvoiceItems((prev) =>
        prev.map((item) =>
          item.id === invoiceItem.id ? { ...item, isSubmitting: true } : item
        )
      );
      const response = await invoicesModule.deleteItem({
        id: invoiceItem.id,
        modifiedOn: invoiceItem.modifiedOn,
      });
      if (response) {
        clearItemVerification(invoiceItem.id);
        updateInvoiceFormState(invoiceItem.id, null);
        fetchAndEnsureDraftInvoice();
        getPurchaseOrders();
      }
    };
    showConfirmModal({
      title: "Delete Invoice Item?",
      description:
        "Are you sure you want to delete this invoice item? This action cannot be undone.",
      confirmButtonText: "Delete",
      titleIcon: {
        path: mdi.mdiTrashCanOutline,
        color: theme.palette.error.main,
      },
      onConfirm: executeDelete,
      buttonPositiveColor: BUTTON_COLORS.ERROR,
    });
  };

  const handleDeletePaymentItem = async (paymentItem) => {
    if (paymentItem.tempId) {
      setTempPaymentItems((prev) =>
        prev.filter((item) => item.id !== paymentItem.id)
      );

      clearItemVerification(paymentItem.id);
      updateInvoiceFormState(paymentItem.id, null);
      return;
    }
    const paymentItemId = paymentItem.billingCommitmentId + invoice.id;
    const executeDelete = async () => {
      setPaymentItems((prev) =>
        prev.map((item) =>
          item.id === paymentItem.id ? { ...item, isSubmitting: true } : item
        )
      );
      const response = await invoicesModule.removePaymentFromInvoice({
        invoiceId: invoice.id,
        data: {
          amount: paymentItem.billingCommitmentAmount,
          billingCommitmentId: paymentItem.billingCommitmentId,
          modifiedOn: invoice.modifiedOn,
        },
      });
      if (response) {
        clearItemVerification(paymentItemId);
        updateInvoiceFormState(paymentItemId, null);
        fetchAndEnsureDraftInvoice();
        getPurchaseOrders();
      }
    };
    showConfirmModal({
      title: "Delete Payment Item?",
      description:
        "Are you sure you want to delete this payment item? This action cannot be undone.",
      confirmButtonText: "Delete",
      titleIcon: {
        path: mdi.mdiTrashCanOutline,
        color: theme.palette.error.main,
      },
      onConfirm: executeDelete,
      buttonPositiveColor: BUTTON_COLORS.ERROR,
    });
  };

  const handleDeleteInvoice = async () => {
    const isTempInvoice = invoice?.isNew;

    if (isTempInvoice) {
      setLocalInvoiceItems([]);
      setTempInvoiceItems([]);
      setInvoices((prev) => prev.filter((inv) => inv !== invoice));

      return;
    }
    const executeDelete = async () => {
      await invoicesModule.delete({
        id: invoice.id,
        modifiedOn: invoice.modifiedOn,
        toastMessageConfig: {
          success: {
            text: "Invoice deleted successfully.",
          },
        },
      });

      if (invoice?.invoiceItems) {
        invoice.invoiceItems.forEach((item) => {
          clearItemVerification(item.id);
        });
      }
      if (invoice?.invoiceBillingCommitments) {
        invoice.invoiceBillingCommitments.forEach((item) => {
          clearItemVerification(item.billingCommitmentId + invoice.id);
        });
      }
      fetchAndEnsureDraftInvoice();
      getPurchaseOrders();
    };
    if (!invoice?.invoiceItems) {
      executeDelete();
      return;
    }
    showConfirmModal({
      title: "Delete Invoice Draft",
      titleIcon: {
        path: mdi.mdiTrashCanOutline,
        color: theme.palette.error.main,
      },
      description:
        "Are you sure you would like to delete this invoice? This action cannot be undone and will remove all data entered.",
      confirmButtonText: "Delete Invoice Draft",
      onConfirm: executeDelete,
      buttonPositiveColor: BUTTON_COLORS.ERROR,
    });
  };

  const handleUpdateInvoicePayment = async (updatedPayment) => {
    try {
      if (updatedPayment.tempId) {
        setTempPaymentItems((prev) =>
          prev.map((item) =>
            item.tempId === updatedPayment.tempId
              ? { ...updatedPayment, isSubmitting: true }
              : item
          )
        );
        const currentInvoiceId = await ensureInvoiceExists();
        const data = await invoicesModule.addPaymentToInvoice({
          invoiceId: currentInvoiceId,
          data: {
            amount: updatedPayment.paidOnBillingCommitment,
            billingCommitmentId: updatedPayment.billingCommitmentId,
            modifiedOn: invoice.modifiedOn,
          },
        });

        if (data) {
          clearItemVerification(updatedPayment.id);
          updateInvoiceFormState(updatedPayment.id, null);

          setPaymentItems(data.invoiceBillingCommitments);

          setTempPaymentItems((prev) =>
            prev.filter((item) => item.id !== updatedPayment.id)
          );
        } else {
          setTempPaymentItems((prev) =>
            prev.map((item) =>
              item.tempId === updatedPayment.tempId
                ? { ...item, isSubmitting: false }
                : item
            )
          );
          throw new Error("Failed to add payment to invoice");
        }
      } else {
        // Handle updating existing payment
        setPaymentItems((prev) =>
          prev.map((item) =>
            item.billingCommitmentId === updatedPayment.billingCommitmentId
              ? { ...updatedPayment, isSubmitting: true }
              : item
          )
        );

        const success = await invoicesModule.updatePaymentFromInvoice({
          invoiceId: invoice.id,
          data: {
            amount: updatedPayment.paidOnBillingCommitment,
            billingCommitmentId: updatedPayment.billingCommitmentId,
            modifiedOn: invoice.modifiedOn,
          },
        });

        if (success) {
          setPaymentItems((prev) =>
            prev.map((item) =>
              item.billingCommitmentId === updatedPayment.billingCommitmentId
                ? { ...updatedPayment, isSubmitting: true }
                : item
            )
          );
        } else {
          setPaymentItems((prev) =>
            prev.map((item) =>
              item.billingCommitmentId === updatedPayment.billingCommitmentId
                ? { ...item, isSubmitting: false }
                : item
            )
          );
          throw new Error("Failed to update invoice payment");
        }
      }

      return true;
    } catch (error) {
      bugsnag.notify("Error updating invoice payment:", error);
      throw error;
    } finally {
      await Promise.all([fetchAndEnsureDraftInvoice(), getPurchaseOrders()]);
    }
  };

  const ensureInvoiceExists = async () => {
    let currentInvoiceId = invoice?.id;

    if (!currentInvoiceId) {
      const response = await invoicesModule.create({
        data: { orderId: order.id, name: newInvoiceName || "" },
      });

      if (response) {
        currentInvoiceId = response.id;

        setInvoices((prev) =>
          prev.map((inv) => (inv.isNew && !inv.id ? response : inv))
        );
      } else {
        throw new Error("Failed to create invoice");
      }
    }

    return currentInvoiceId;
  };

  const handleCreateInvoiceItem = async (updatedItem, isItemVerified) => {
    setTempInvoiceItems((prev) =>
      prev.map((item) =>
        item.id === updatedItem.id ? { ...item, isSubmitting: true } : item
      )
    );

    try {
      const currentInvoiceId = await ensureInvoiceExists();

      const data = await invoicesModule.createItem({
        data: {
          ...updatedItem,
          invoiceId: currentInvoiceId,
        },
      });

      if (data) {
        verificationRef.current.set(data.id, isItemVerified);

        setLocalInvoiceItems((prev) => [
          ...prev,
          { ...data, isSubmitting: true, isVerified: isItemVerified },
        ]);

        setTempInvoiceItems((prev) =>
          prev.filter((item) => item.id !== updatedItem.id)
        );

        return true;
      }

      throw new Error("Failed to create invoice item");
    } catch (error) {
      setTempInvoiceItems((prev) =>
        prev.map((item) =>
          item.id === updatedItem.id ? { ...item, isSubmitting: false } : item
        )
      );
      throw error;
    }
  };

  const handleUpdateExistingItem = async (updatedItem, isItemVerified) => {
    if (isItemVerified !== undefined) {
      verificationRef.current.set(updatedItem.id, isItemVerified);
    }

    setLocalInvoiceItems((prev) =>
      prev.map((item) =>
        item.id === updatedItem.id ? { ...item, isSubmitting: true } : item
      )
    );

    try {
      const data = await invoicesModule.updateItem({
        id: updatedItem.id,
        data: updatedItem,
      });

      if (data) {
        setLocalInvoiceItems((prev) =>
          prev.map((item) =>
            item.id === updatedItem.id
              ? { ...updatedItem, isSubmitting: true }
              : item
          )
        );

        return true;
      }

      throw new Error("Failed to update invoice item");
    } catch (error) {
      setLocalInvoiceItems((prev) =>
        prev.map((item) =>
          item.id === updatedItem.id ? { ...item, isSubmitting: false } : item
        )
      );
      throw error;
    }
  };

  const handleUpdateInvoiceItem = async (updatedItem) => {
    try {
      const isItemVerified = updatedItem.isVerified;

      if (updatedItem.tempId) {
        return await handleCreateInvoiceItem(updatedItem, isItemVerified);
      }

      return await handleUpdateExistingItem(updatedItem, isItemVerified);
    } catch (error) {
      bugsnag.notify("Error updating invoice item:", error);
      throw error;
    } finally {
      await Promise.all([fetchAndEnsureDraftInvoice(), getPurchaseOrders()]);
    }
  };

  const showConfirmModal = ({
    title,
    description,
    confirmButtonText = "Confirm",
    cancelButtonText = "",
    onConfirm,
    ...rest
  }) => {
    setConfirmModalConfig({
      open: true,
      title,
      description,
      textPositive: confirmButtonText,
      textNegative: cancelButtonText,
      handlePositive: () => {
        onConfirm();
        closeConfirmModal();
      },
      handleNegative: () => closeConfirmModal(),
      ...rest,
    });
  };

  const closeConfirmModal = () => {
    setConfirmModalConfig((prev) => ({ ...prev, open: false }));
  };

  const countOfVerifiedItems = useMemo(() => {
    if (!localInvoiceItems?.length) return 0;

    const countOfInvoiceItems = localInvoiceItems.filter(
      (item) => verificationState[item.id] === true
    ).length;

    const countOfPaymentItems = paymentItems.filter(
      (item) =>
        verificationState[item.billingCommitmentId + invoice.id] === true
    ).length;
    return countOfInvoiceItems + countOfPaymentItems;
  }, [localInvoiceItems, verificationState]);

  const isInvoiceVerified = useMemo(() => {
    return (
      countOfVerifiedItems === localInvoiceItems.length + paymentItems.length &&
      localInvoiceItems.length > 0 &&
      paymentItems.length > 0
    );
  }, [countOfVerifiedItems, localInvoiceItems, paymentItems]);

  const handleVoidInvoice = async () => {
    const voidables = await invoicesModule.checkInvoiceVoidable({
      id: invoice.id,
    });
    const excludedDraftInvoices = voidables.filter(
      (inv) => inv.status !== InvoiceStatus.Draft
    );
    let hasAnyBeenPaid = false;
    let modalHeaderText =
      "Voiding an invoice will delete all items and relationships for those items. This cannot be undone.";

    let modalDescription = null;
    if (excludedDraftInvoices.length > 0) {
      hasAnyBeenPaid = voidables.some((inv) => inv.hasPayments);
      modalHeaderText = hasAnyBeenPaid
        ? "Unable to void this invoice as payment has been posted against a related invoice."
        : "This action will void the invoice and all of its items. This cannot be undone. It will additionally effect or void any related invoices as well.";
      modalDescription = (
        <Box
          p={1}
          mt={1}
          gap={2}
          backgroundColor={hasAnyBeenPaid ? "error.light" : "warning.light"}
          borderRadius={1}
          display="flex"
          flexDirection="row"
          alignItems="center"
          justifyContent="space-between"
        >
          <MiIcon
            path={
              hasAnyBeenPaid ? mdi.mdiAlertCircleOutline : mdi.mdiAlertOutline
            }
            color={
              hasAnyBeenPaid
                ? theme.palette.error.main
                : theme.palette.warning.main
            }
            size={0.8}
          />
          <Box>
            {hasAnyBeenPaid ? (
              <>
                <Typography fontWeight="600" variant="body2">
                  Related invoices has payment posted.
                </Typography>
                <Typography
                  fontWeight="400"
                  variant="body2"
                  color="text.secondary"
                  mt={1}
                >
                  Related invoice(s):{" "}
                  <Typography component="span" variant="body2" fontWeight="600">
                    {listRelatedInvoices(voidables)}
                  </Typography>{" "}
                  has/have had a payment posted.
                </Typography>
              </>
            ) : (
              <>
                <Typography fontWeight="600" variant="body2">
                  Related invoices will be voided unless payment has been
                  applied.
                </Typography>
                <Typography
                  fontWeight="400"
                  variant="body2"
                  mt={1}
                  color="text.secondary"
                >
                  Related invoice:{" "}
                  <Typography component="span" variant="body2" fontWeight="600">
                    {listRelatedInvoices(voidables)}
                  </Typography>{" "}
                  will be voided.
                </Typography>
              </>
            )}
          </Box>
        </Box>
      );
    }

    const executeVoidInvoice = async () => {
      await invoicesModule.voidInvoice({
        id: invoice.id,
        modifiedOn: invoice.modifiedOn,
      });

      if (invoice?.invoiceItems) {
        invoice.invoiceItems.forEach((item) => {
          clearItemVerification(item.id);
        });
      }
      if (invoice?.invoiceBillingCommitments) {
        invoice.invoiceBillingCommitments.forEach((item) => {
          clearItemVerification(item.billingCommitmentId + invoice.id);
        });
      }
      fetchAndEnsureDraftInvoice();
    };

    showConfirmModal({
      title: "Void Invoice",
      titleIcon: {
        path: mdi.mdiTrashCanOutline,
        color: theme.palette.error.main,
      },
      description: modalHeaderText,
      children: modalDescription,
      confirmButtonText: "Void Invoice",
      disableClick: hasAnyBeenPaid,
      onConfirm: executeVoidInvoice,
      buttonPositiveColor: BUTTON_COLORS.ERROR,
    });
  };

  return (
    <Box mx={4} mb={2}>
      <Box className={classes.cardContainer}>
        <Box className={classes.cardHeader}>
          <span className={classes.count}>{invoiceHeaderNumber}</span>
          {invoice?.isNew ? (
            <MiInputTextbox
              size="small"
              variant="outlined"
              label="Invoice Title"
              required
              disabled={tempInvoiceItems.length > 0}
              error={invoiceNameError}
              sx={{ width: 240, marginLeft: 2 }}
              onChange={(e) => {
                setNewInvoiceName(e.target.value);
                if (invoiceNameError) {
                  setInvoiceNameError(null);
                }
              }}
              value={newInvoiceName}
            />
          ) : (
            <>
              <Typography
                variant="body1"
                fontWeight="bold"
                ml={2}
                color="text.primary"
              >
                {invoiceName || ""}
              </Typography>
              <Typography
                variant="body2"
                fontWeight="light"
                ml={2}
                color="text.secondary"
              >
                {invoiceNumber || ""}
              </Typography>
              {invoice.status === InvoiceStatus.Voided && (
                <Box
                  bgcolor="#F44336"
                  border="1px solid #D32F2F"
                  px={1}
                  mx={2}
                  borderRadius={1}
                >
                  <Typography color="white" fontSize={11}>
                    Voided :{" "}
                    {formatDate(
                      invoice.voidDate,
                      DATE_TIME_FORMATS.FORMAT_MM_DD_YY
                    )}
                  </Typography>
                </Box>
              )}
              <Typography
                variant="body2"
                fontWeight="light"
                ml={4}
                color="text.secondary"
              >
                {invoice?.systemNotes || ""}
              </Typography>
            </>
          )}
          <Box ml="auto" mr={2} display="flex" alignItems="center" gap={4}>
            {isInvoiceMode && invoice.status === InvoiceStatus.Draft && (
              <Box
                border="2px solid"
                borderColor={isInvoiceVerified ? "success.main" : "transparent"}
                backgroundColor={
                  isInvoiceVerified ? "green.light" : "grey.mediumLight"
                }
                borderRadius={1}
                px={2}
              >
                <MiCheckbox
                  value
                  color="success"
                  style={{ cursor: "default", padding: theme.spacing(1, 0) }}
                  disableRipple
                  readOnly
                  labelStyle={{
                    "& .MuiFormControlLabel-label": {
                      color: isInvoiceVerified ? "success.main" : "inherit",
                      fontWeight: "400",
                      paddingLeft: 1,
                    },
                  }}
                  label={`${countOfVerifiedItems}/${
                    localInvoiceItems.length + paymentItems.length
                  } items verified`}
                  size="small"
                />
              </Box>
            )}

            {viewMode === INVOICING_ROUTES.INVOICED && (
              <MiButton
                title="Void Invoice"
                startIcon={{ path: mdi[ICONS_WEB.CANCEL] }}
                variant={BUTTON_VARIANTS.OUTLINED}
                disabled={
                  invoice.status === InvoiceStatus.Voided ||
                  invoice.status === InvoiceStatus.Paid
                }
                color="error"
                onClick={handleVoidInvoice}
              />
            )}
          </Box>
          {viewMode !== INVOICING_ROUTES.INVOICED &&
          invoice.status === InvoiceStatus.Draft &&
          (invoicesCount > 1 || invoice.isNew) ? (
            <MiIconButton onClick={handleDeleteInvoice}>
              <MiIcon
                color={theme.palette.red.main}
                path={mdi.mdiTrashCanOutline}
              />
            </MiIconButton>
          ) : null}
        </Box>
        <Typography
          variant="body2"
          color="text.primary"
          fontWeight="500"
          my={1}
        >
          Items
        </Typography>

        <Box className={classes.itemRow}>
          {filteredInvoiceItems.map((item) => (
            <AccountingInvoiceItem
              invoiceStatus={invoice.status}
              key={item.id || item.tempId}
              item={item}
              itemType="invoice"
              onDelete={() => handleDeleteInvoiceItem(item)}
              onUpdate={handleUpdateInvoiceItem}
            />
          ))}
        </Box>
        {filteredInvoiceItems.length === 0 && (
          <Box p={2} textAlign="center">
            <Typography variant="body2" color="text.secondary">
              No items added yet.
            </Typography>
          </Box>
        )}
        {localInvoiceVarianceItems.length > 0 && (
          <>
            <Typography
              variant="body2"
              color="text.primary"
              fontWeight="500"
              my={1}
            >
              Variances
            </Typography>

            <Box className={classes.itemRow}>
              {localInvoiceVarianceItems.map((item) => (
                <AccountingInvoiceItem
                  invoiceStatus={invoice.status}
                  key={item.id || item.tempId}
                  item={item}
                  itemType="invoice"
                  readOnly
                />
              ))}
            </Box>
          </>
        )}

        <Typography
          variant="body2"
          color="text.primary"
          fontWeight="500"
          my={1}
        >
          Payment Information
        </Typography>

        <Box className={classes.itemRow}>
          {combinedInvoicePayments.map((item) => (
            <AccountingInvoiceItem
              invoiceStatus={invoice.status}
              key={item.id || item.tempId}
              item={item}
              itemType="payment"
              onDelete={() => handleDeletePaymentItem(item)}
              onUpdate={handleUpdateInvoicePayment}
            />
          ))}
          {combinedInvoicePayments.length === 0 && (
            <Box p={2} textAlign="center">
              <Typography variant="body2" color="text.secondary">
                No payment information added yet.
              </Typography>
            </Box>
          )}
        </Box>

        <Box
          display="flex"
          flexDirection="column"
          alignItems="center"
          bgcolor="common.white"
          width="100%"
        >
          {localInvoiceVarianceItems.length > 0 && (
            <Box
              bgcolor="info.light"
              display="flex"
              alignItems="center"
              p={0.5}
              gap={1}
              width="100%"
            >
              <MiIcon
                path={mdi.mdiInformationOutline}
                color={theme.palette.info.main}
                size={0.8}
              />
              <Typography
                fontWeight="400"
                variant="body2"
                color="text.primary"
                mr={2}
              >
                Variance items are not included in the total price and variance
                calculation.
              </Typography>
            </Box>
          )}
          <Box
            display="flex"
            flexWrap="wrap"
            width="100%"
            alignItems="center"
            gap={4}
            p={2}
            justifyContent="flex-end"
          >
            <Typography fontWeight="bold" variant="body1" color="grey.medium">
              Total Price:
            </Typography>
            <Typography
              fontWeight="600"
              color={totalDraw < 0 ? "error.main" : "inherit"}
            >
              {formatOpenBalanceDisplay(totalDraw)}
            </Typography>
            <Typography fontWeight="bold" variant="body1" color="grey.medium">
              Discounts:
            </Typography>
            <Typography fontWeight="600">
              {formatOpenBalanceDisplay(invoice.totalDiscounts)}
            </Typography>
            <Typography fontWeight="bold" variant="body1" color="grey.medium">
              Total Paid:
            </Typography>
            <Typography
              fontWeight="600"
              color={totalPaidOnPO < 0 ? "error.main" : "inherit"}
            >
              {formatOpenBalanceDisplay(totalPaidOnPO)}
            </Typography>
            <Typography fontWeight="bold" variant="body1" color="grey.medium">
              Variance:
            </Typography>
            <Typography
              fontWeight="600"
              color={getVarianceColor(totalVariance)}
            >
              {formatOpenBalanceDisplay(totalVariance)}
            </Typography>
          </Box>
        </Box>
      </Box>
      <Box display="flex" justifyContent="flex-end" gap={2} mt={2}>
        {(activeStep === RECONCILIATION_STEPS.RECONCILIATION ||
          (isInvoiceMode && invoice.status === InvoiceStatus.Draft)) && (
          <MiButton
            title="Add Item"
            startIcon={{ path: mdi[ICONS_WEB.PLUS] }}
            variant={BUTTON_VARIANTS.OUTLINED}
            disabled={tempInvoiceItems.length > 0}
            color="primary"
            className={classes.actionButton}
            onClick={handleAddInvoiceItem}
          />
        )}
        {(activeStep === RECONCILIATION_STEPS.RECONCILIATION ||
          (isInvoiceMode && invoice.status === InvoiceStatus.Draft)) && (
          <MiButton
            title="Attach Payment Information"
            startIcon={{ path: mdi[ICONS_WEB.PLUS] }}
            variant={BUTTON_VARIANTS.OUTLINED}
            disabled={tempPaymentItems.length > 0}
            color="primary"
            className={classes.actionButton}
            onClick={handleAddPaymentInformation}
          />
        )}
      </Box>
      <MiModalConfirm
        open={confirmModalConfig.open}
        title={confirmModalConfig.title}
        description={confirmModalConfig.description}
        textPositive={confirmModalConfig.textPositive}
        disableClick={confirmModalConfig.disableClick}
        textNegative={confirmModalConfig.textNegative}
        handlePositive={confirmModalConfig.handlePositive}
        handleNegative={confirmModalConfig.handleNegative}
        buttonPositiveColor={confirmModalConfig.buttonPositiveColor}
        titleIcon={confirmModalConfig.titleIcon}
      >
        {confirmModalConfig.children}
      </MiModalConfirm>
    </Box>
  );
};

const useStyles = makeStyles()((theme) => ({
  root: {
    height: "100%",
    display: "flex",
    flexDirection: "column",
  },
  cardContainer: {
    backgroundColor: theme.palette.grey.light,
    border: `1px solid ${theme.palette.grey.accent}`,

    borderRadius: theme.spacing(1),
    overflow: "hidden",
    display: "flex",
    flexDirection: "column",
    margin: theme.spacing(1, 0),
    padding: theme.spacing(2),
  },
  cardHeader: {
    display: "flex",
    justifyContent: "flex-start",
    alignItems: "center",
    padding: theme.spacing(0.5, 0),
    marginBottom: theme.spacing(0.5),
  },
  itemRow: {
    display: "flex",
    flexDirection: "column",
    borderRadius: 4,
    margin: theme.spacing(1, 0),
  },
  count: {
    backgroundColor: theme.palette.grey.main,
    color: theme.palette.common.white,
    fontWeight: 500,
    borderRadius: "9999px",
    minWidth: 20,
    height: 20,
    padding: theme.spacing(0, 1.5),
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    fontSize: theme.fontSize.small,
    lineHeight: 1,
  },
  actionButton: {
    borderWidth: "2px !important",
    borderColor: `${theme.palette.primary.main} !important`,
    padding: theme.spacing(1, 2.5),
    fontWeight: 600,
    "&:hover": {
      borderWidth: "2px !important",
    },
  },
}));
